import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { COA, FileRecord } from '@twinsketch/topika-model';
import ErrorModal from 'src/components/common/ErrorModal';
import 'src/styles/workOrder/work-order.css';

interface COAFormProps {
  coaData: COA[];
  files: FileRecord[];
}

const COAForm: React.FC<COAFormProps> = ({ coaData, files }) => {
  const { id } = useParams<{ id?: string }>();
  const [error, setError] = useState<string | null>(null);
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());

  const toggleExpand = (index: number) => {
    setExpandedIndices(prev => {
      const updated = new Set(prev);
      updated.has(index) ? updated.delete(index) : updated.add(index);
      return updated;
    });
  };

  return (
    <div className="content-wrapper">
      <ErrorModal message={error} onClose={() => setError(null)} />

      <h2>Certificate of Analysis</h2>
      <div className="table-wrapper">
        <table className="mixing-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th></th>
              <th>PO Number</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Production Dates</th>
              <th>Inspection Date</th>
              <th>Inspected By</th>
              <th>Approved By</th>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            {coaData.length > 0 ? (
              coaData.map((work, index) => {
                const extractedFrom = work.extractedFrom;
                const matchedFile = extractedFrom?.sourceFileId
                  ? files.find(file => file.id === extractedFrom?.sourceFileId)
                  : null;

                return (
                  <React.Fragment key={index}>
                    <tr>
                      <td>
                        <button
                          onClick={() => toggleExpand(index)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          {expandedIndices.has(index) ? '−' : '+'}
                        </button>
                      </td>
                      <td>{work.poNumber}</td>
                      <td>{work.issueDate}</td>
                      <td>{work.customerName}</td>
                      <td>{work.productionDates.join(' / ')}</td>
                      <td>{work.inspectionDate}</td>
                      <td>{work.inspectedBy}</td>
                      <td>{work.approvedBy}</td>
                      <td>
                        {matchedFile ? (
                          <span style={{ color: 'green' }}>{matchedFile.fileName}</span>
                        ) : (
                          'No File'
                        )}
                      </td>
                    </tr>

                    {expandedIndices.has(index) && (
                      <tr>
                        <td colSpan={11}>
                          <div
                            style={{
                              padding: '12px',
                              backgroundColor: '#fafafa',
                              borderTop: '1px solid #ccc',
                            }}
                          >
                            <strong>Material Info</strong>
                            <table style={{ width: '100%', marginBottom: '1rem' }}>
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>Name</th>
                                  <th>Composition (%)</th>
                                  <th>Lot No</th>
                                </tr>
                              </thead>
                              <tbody>
                                {work.materialsInfo.map((mat, i) => (
                                  <tr key={i}>
                                    <td>{mat.type}</td>
                                    <td>{mat.name}</td>
                                    <td>{mat.compositionPercentage}</td>
                                    <td>{mat.lotNo}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            <strong>Product Info</strong>
                            <table style={{ width: '100%', marginBottom: '1rem' }}>
                              <thead>
                                <tr>
                                  <th>Lot No</th>
                                  <th>Foil Width</th>
                                  <th>Tab Front</th>
                                  <th>Coating Width</th>
                                  <th>Tab Back</th>
                                  <th>Misalignment</th>
                                  <th>Length (m)</th>
                                  <th>Remark</th>
                                </tr>
                              </thead>
                              <tbody>
                                {work.productInfo.map((product, j) => (
                                  <tr key={j}>
                                    <td>{product.lotNo}</td>
                                    <td>{product.measuredDimension.foilWidth}</td>
                                    <td>{product.measuredDimension.tabLengthFront}</td>
                                    <td>{product.measuredDimension.coatingWidth}</td>
                                    <td>{product.measuredDimension.tabLengthBack}</td>
                                    <td>{product.misalignment?.status}</td>
                                    <td>{product.lengthMeterPerRoll}</td>
                                    <td>{product.remark ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', color: 'gray' }}>
                  No COA Data Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default COAForm;
